/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */
import { ConfigClient } from "../../clients/configClient";
import { ResponderMessage, RouterClient } from "../../clients/routerClient";
import { LauncherClient } from "../../clients/launcherClient";
import { Logger } from "../../clients/logger";
import { StorageClient } from "../../clients/storageClient";
import { Globals } from "../../common";
import { StandardError } from "../../types";
import { BaseService } from "../baseService";
import MyCrypto from "./myCrypto";
import { OAuth, Password } from "./OAuth";
import { parallel as asyncParallel } from "async";

Logger.start();

declare let fin: NonNullable<typeof Globals.fin>;
let FINSEMBLE_CONFIG: Record<string, any>;

Logger.system.log("Starting Authentication Service");

/**
 * The Authentication Service authenticates the Finsemble user.

 */
class AuthenticationService extends BaseService {
	firstAuthorization: boolean;

	currentCredentials: null;

	adapters: Record<string, any>;

	constructor() {
		super({
			name: "authenticationService",
			startupDependencies: {},
			shutdownDependencies: {
				services: ["windowService"],
			},
		});

		this.firstAuthorization = true;
		this.currentCredentials = null; // Contains the current credentials
		this.adapters = {};
		this.adapters.PASSWORD = new Password();
		this.adapters.OAUTH2 = new OAuth();

		this.startAuthentication = this.startAuthentication.bind(this);
		this.startServingSignOn = this.startServingSignOn.bind(this);

		this.onBaseServiceReady((done: () => void) => {
			this.doInit(done);
		});

		this.start();
	}

	doInit(cb: () => void): void {
		Logger.system.debug("APPLICATION LIFECYCLE:STARTUP:authenticationService: initialization started.");

		const { startAuthentication, startServingSignOn } = this;

		ConfigClient.getValue({ field: "finsemble" }, (getValueErr, config) => {
			FINSEMBLE_CONFIG = config;

			// It is expensive to listen for all config changes, so only listen for the specific config changes authentication uses.  Update local copy if they change.
			ConfigClient.addListener({ field: "finsemble.isAuthEnabled" }, (addListenerErr, data) => {
				FINSEMBLE_CONFIG.isAuthEnabled = data.value;
			});
			ConfigClient.addListener({ field: "finsemble.authentication" }, (addListenerErr, data) => {
				FINSEMBLE_CONFIG.authentication = data.value;
			});
			ConfigClient.addListener({ field: "finsemble.autoSignOn" }, (addListenerErr, data) => {
				FINSEMBLE_CONFIG.autoSignOn = data.value;
			});

			asyncParallel([startAuthentication, startServingSignOn], cb);
		});
	}

	// these functions support the initial integration handshake
	startAuthentication(startCallBack: Function): void {
		Logger.system.debug("starting Authentication");
		RouterClient.addPubSubResponder("Authorization", {});
		RouterClient.publish("AuthorizationState", { state: "waiting" });

		RouterClient.subscribe("Authorization", (err, response) => {
			if (err) {
				Logger.system.error(`Failed Authorization Notification: ${JSON.stringify(err)}`);
			} else {
				// spawn successful so wait for response
				Logger.system.info(`Authorization Notification: ${JSON.stringify(response.data)}`);
			}
		});

		const waitForAuthorization = (): void => {
			Logger.system.log("APPLICATION LIFECYCLE:STARTUP:Waiting for Authorization");
			RouterClient.addListener("AuthenticationService.authorization", (error, response) => {
				RouterClient.publish("AuthorizationState", { state: "done" }); // signal service manager that auth is done (so can adjust timeout timer)
				if (error) {
					Logger.system.error(
						`APPLICATION LIFECYCLE:STARTUP:Error on incoming authorization: ${JSON.stringify(error)}`
					);
				} else {
					// log out the user name and the credential keys returned but NOT the values
					const authResponseLog = {
						user: response.data && response.data.user ? response.data.user : "no user",
						credentials:
							response.data && response.data.credentials ? Object.keys(response.data.credentials) : "no credentials",
					};
					Logger.system.log(`APPLICATION LIFECYCLE:STARTUP:Authorization received: ${JSON.stringify(authResponseLog)}`);
					if (this.firstAuthorization) {
						this.firstAuthorization = false;
						startCallBack();
					}

					const authorization = (this.currentCredentials = response.data);
					StorageClient.setUser({ user: authorization.user }, () => {
						RouterClient.publish("Authorization", authorization);
					});
				}
			});
		};

		// This is where we check for initial authentication at boot time. If authentication is required then
		// the auth process will kick off, waiting for completion in waitForAuthorization.
		const { isAuthEnabled } = FINSEMBLE_CONFIG;
		Logger.system.log("finsemble.isAuthEnabled", isAuthEnabled);
		const config = FINSEMBLE_CONFIG.authentication;
		// If either isAuthEnabled (legacy username/password) or config["startup"] then we will
		// ask the user for credentials before starting up finsemble
		RouterClient.publish("AuthorizationState", { state: "starting" }); // signal service manager that auth is starting (so can adjust timeout timer)

		// bypass startup authentication if SmartDesktopDesigner
		fin.desktop.System.getSDServerInfo((info) => {
			const sddIsEnabled = info.enabled;

			if (!sddIsEnabled && (isAuthEnabled || (config && config.startup))) {
				const params = {
					profile: "startup",
					isAuthEnabled,
				};
				this.beginAuthentication(params, () => {});
				// Wait out-of-band for authorization to complete
				waitForAuthorization();
			} else {
				Logger.system.info("Authentication Disabled");
				const defaultUser = "defaultUser";
				StorageClient.setUser({ user: defaultUser });
				RouterClient.publish("Authorization", {
					user: defaultUser,
					credentials: { token: "defaultAuthorization" },
				});
				RouterClient.publish("AuthorizationState", { state: "done" }); // signal service manager that auth is starting (so can adjust timeout timer)
				startCallBack();
			}
		});
	}

	// Kicks off an authentication process through an adapter.
	beginAuthentication(params: Record<string, any>, cb: Function): void {
		Logger.system.debug("APPLICATION LIFECYCLE:STARTUP:AuthenticationService:beginAuthentication");

		const config = FINSEMBLE_CONFIG.authentication;
		let authenticationProfile;
		if (config && config[params.profile]) {
			// Create a safe clone of the authenticationProfile
			authenticationProfile = { ...config[params.profile] };
		} else {
			// Make a blank authenticationProfile, to support legacy isAuthEnabled
			authenticationProfile = {};
		}

		authenticationProfile.name = params.profile;
		if (!authenticationProfile.component) {
			const err = `AuthenticationService missing "component" entry`;
			cb(err);
			return Logger.system.error(err);
		}
		if (!authenticationProfile.adapter) {
			const err = `AuthenticationService missing "adapter" entry`;
			cb(err);
			return Logger.system.error(err);
		}
		// We'll examine the config type and farm out the work to the appropriate handler
		const { adapter } = authenticationProfile;
		if (this.adapters[adapter]) {
			this.adapters[adapter].authenticate(authenticationProfile, params.spawnParams, cb);
		} else {
			Logger.system.error(`Cannot find authentication adapter: ${adapter}`);
		}
	}

	// these functions support single sign on capability
	startServingSignOn(startSignOnCallback: Function): void {
		Logger.system.debug("APPLICATION LIFECYCLE:STARTUP:AuthenticationService:startServingSignOn");
		const activeSignOns: Record<string, any> = {};
		const nowSignedOn: Record<string, any> = {};
		const acceptTimers: Record<string, any> = {};
		let myCrypto: MyCrypto; // for instance of crypto object
		const keyPart1 = "mySimpleTest";
		const keyPart2 = "Key2";
		let autoSignOnConfig: Record<string, any>;

		// if component doesn't send an expected response (e.g. accept or reject), this timeout fires to automatically reject the sign on data
		function setUpResponseTimeout(signOnKey: string) {
			Logger.system.debug(
				"APPLICATION LIFECYCLE:STARTUP:AuthenticationService:setupResponseTimeout:acceptTimeout started",
				signOnKey
			);
			const acceptTimeoutDuration = 60 * 1000;
			const timerSignOnKey = signOnKey; // closure key used for this specific timer
			function acceptTimeout() {
				Logger.system.debug("APPLICATION LIFECYCLE:STARTUP:AuthenticationService:acceptTimeout fired", timerSignOnKey);
				RouterClient.transmit("authentication.appRejectSignOn", {
					signOnKey: timerSignOnKey,
				}); // force a rejection since timed out
			}
			acceptTimers[signOnKey] = setTimeout(acceptTimeout, acceptTimeoutDuration);
		}
		function clearResponseTimeout(signOnKey: string) {
			Logger.system.debug("APPLICATION LIFECYCLE:STARTUP:AuthenticationService:acceptTimeout cleared", signOnKey);
			clearTimeout(acceptTimers[signOnKey]);
		}

		// used in different context to complete a sign on request
		function continueSignOn(
			queryEvent: ResponderMessage<any>,
			signOnKey: string,
			params: Record<string, any>,
			signOnData: Record<string, any> | null,
			retry: boolean
		) {
			Logger.system.debug(
				"APPLICATION LIFECYCLE:STARTUP:AuthenticationService:continueSignOn",
				queryEvent,
				signOnKey,
				params,
				signOnData,
				retry
			);
			let activeSignOnObject = activeSignOns[signOnKey]; // lookup sign on object for give key (if it exists)
			const isSignedOn = nowSignedOn[signOnKey]; // find out if sign on has already accepted for this key
			if (activeSignOnObject === undefined || retry) {
				// if not in the middle of a sign on handshake
				activeSignOnObject = activeSignOnObject || { params, queuedQuerys: [] };
				activeSignOnObject.mainQueryEvent = queryEvent;
				if (signOnData) {
					// if sign on data has already been retrieved from storage earlier
					Logger.system.debug(
						"APPLICATION LIFECYCLE:STARTUP:AuthenticationService:continueSignOn using stored signOnData",
						signOnData
					);
					if (isSignedOn) {
						signOnData.validationRequired = false;
					} else {
						setUpResponseTimeout(signOnKey);
						signOnData.validationRequired = true;
						activeSignOns[signOnKey] = activeSignOnObject;
					}
					activeSignOnObject.signOnData = signOnData; // will use if accepted
					queryEvent.sendQueryResponse(null, signOnData); // send back to first app requesting this signup, which will accept or reject
				} else {
					Logger.system.debug(
						"APPLICATION LIFECYCLE:STARTUP:AuthenticationService:authentication.appSignOn spawning dialog"
					);
					LauncherClient.spawn("dialogSignOn", { data: { signOnKey, params } }, (err) => {
						if (err) {
							Logger.system.error(
								`APPLICATION LIFECYCLE:STARTUP:AuthenticationService:Failed To Spawn Authentication Component: ${JSON.stringify(
									err
								)}`
							);
							queryEvent.sendQueryResponse(err, null);
							for (let i = 0; i < activeSignOnObject.queuedQuerys.length; i++) {
								activeSignOnObject.queuedQuerys[i].sendQueryResponse("SignOn Rejected and Terminating", null);
							}
							delete activeSignOns[signOnKey];
						} else {
							// spawn successful so wait for response
							Logger.system.debug(
								"APPLICATION LIFECYCLE:STARTUP:AuthenticationService:Authentication SignOn Successfully Spawned"
							);
						}
					});
					activeSignOns[signOnKey] = activeSignOnObject;
				}
			} else {
				// already in the  middle of an active sign on, so queue this one
				Logger.system.debug(
					"APPLICATION LIFECYCLE:STARTUP:AuthenticationService:continueSignOn queueing request",
					queryEvent
				);
				// wait to respond to the other queries with same signOnKey after the first one is complete
				activeSignOnObject.queuedQuerys.push(queryEvent);
			}
		}

		function getSignOnStorageKey(signOnKey: string): string {
			return `SignOn.${signOnKey}`;
		}

		// handles client beginAuthentication requests
		RouterClient.addResponder("authentication.beginAuthentication", (err, queryEvent) => {
			if (!queryEvent) {
				Logger.system.error("No query event received for beginAuthentication");
				return;
			}
			this.beginAuthentication(
				queryEvent.data,
				(beginAuthenticationError: StandardError, result: Record<string, any>) => {
					queryEvent.sendQueryResponse(beginAuthenticationError, result);
				}
			);
		});

		// handles client appSignOn request
		RouterClient.addResponder("authentication.appSignOn", (err, queryEvent) => {
			if (!queryEvent) {
				Logger.system.error("No query event received for appSignOn");
				return;
			}
			const { signOnKey } = queryEvent.data;
			const { params } = queryEvent.data;
			Logger.system.debug("APPLICATION LIFECYCLE:STARTUP:authentication.appSignOn", signOnKey, params, queryEvent);

			if (!params.force) {
				if (signOnKey in autoSignOnConfig.hardcoded) {
					// if hard coded key in config then use it
					const hardcodedSignon = autoSignOnConfig.hardcoded[signOnKey];
					hardcodedSignon.signOnKey = signOnKey; // add the key to the object since will be returned to component
					Logger.system.debug(
						`APPLICATION LIFECYCLE:STARTUP:authentication.appSignOn hardcoded ${JSON.stringify(hardcodedSignon)}`
					);
					continueSignOn(queryEvent, signOnKey, params, hardcodedSignon, false);
				} else {
					StorageClient.get(
						{ topic: "finsemble", key: getSignOnStorageKey(signOnKey) },
						(storageErr, encryptedData) => {
							if (encryptedData) {
								// if signon info in storage
								// var parsedEncryptedData = JSON.parse(encryptedData);
								myCrypto.decryptString(encryptedData, (signOnData: string) => {
									continueSignOn(queryEvent, signOnKey, params, JSON.parse(signOnData), false);
								});
							} else {
								// else continue and prompt
								continueSignOn(queryEvent, signOnKey, params, null, false);
							}
						}
					);
				}
			} else {
				Logger.system.debug("APPLICATION LIFECYCLE:STARTUP:authentication.appSignOn forcing signOn");
				continueSignOn(queryEvent, signOnKey, params, null, false);
			}
		});

		// handles client appRejectAndRetrySignOn request
		RouterClient.addResponder("authentication.appRejectAndRetrySignOn", (err, queryEvent) => {
			if (!queryEvent) {
				Logger.system.error("No query event received for appRejectAndRetrySignOn");
				return;
			}
			Logger.system.debug("APPLICATION LIFECYCLE:STARTUP:authentication.appRejectAndRetrySignOn", err, queryEvent);
			const { signOnKey } = queryEvent.data;
			const activeSignOnObject = activeSignOns[signOnKey];
			if (activeSignOnObject !== undefined) {
				// start again using the previous signon params
				clearResponseTimeout(signOnKey);
				const { params } = activeSignOnObject;
				params.userMsg = queryEvent.data.params ? queryEvent.data.params.userMsg : null;
				nowSignedOn[signOnKey] = false;
				continueSignOn(queryEvent, signOnKey, params, null, true);
			} else {
				Logger.system.warn(
					"APPLICATION LIFECYCLE:STARTUP:authentication.appRejectAndRetrySignOn for inactive signOnKey:",
					signOnKey
				);
			}
		});

		// Handles requests for current credentials
		RouterClient.addResponder("authentication.currentCredentials", (err, queryEvent) => {
			if (!queryEvent) {
				Logger.system.error("No query event received for currentCredentials");
				return;
			}
			queryEvent.sendQueryResponse(null, this.currentCredentials);
		});

		// handles response from signon dialog spawned by signon request
		RouterClient.addListener("authentication.dialogSignOnToAuthService", (err, signOnEvent) => {
			Logger.system.debug("APPLICATION LIFECYCLE:STARTUP:authentication.dialogSignOnToAuthService", err, signOnEvent);
			const signOnData = signOnEvent.data;
			const activeSignOnObject = activeSignOns[signOnData.signOnKey];
			err = err || signOnData.error || null;
			activeSignOnObject.signOnData = signOnData; // will use if accepted
			const queryEvent = activeSignOnObject.mainQueryEvent;
			if (!err) {
				setUpResponseTimeout(signOnData.signOnKey);
				signOnData.validationRequired = true;
				Logger.system.debug(
					`APPLICATION LIFECYCLE:STARTUP:authentication.dialogSignOnToAuthService response${JSON.stringify(signOnData)}`
				);
				queryEvent.sendQueryResponse(err, signOnData); // send back to first app requesting this signup, which will accept or reject
			} else {
				queryEvent.sendQueryResponse(err, null); // send back to first app requesting this signup, which will accept or reject
				for (let i = 0; i < activeSignOnObject.queuedQuerys.length; i++) {
					activeSignOnObject.queuedQuerys[i].sendQueryResponse(err, null);
				}
				delete activeSignOns[signOnData.signOnKey]; // apps will have to restart sign ons if error (or kill themsselves)
			}
		});

		// handles client appAcceptSignOn request
		RouterClient.addListener("authentication.appAcceptSignOn", (err, transmitEvent) => {
			Logger.system.debug("APPLICATION LIFECYCLE:STARTUP:authentication.appAcceptSignOn", err, transmitEvent);
			const { signOnKey } = transmitEvent.data;
			const activeSignOnObject = activeSignOns[signOnKey];
			if (activeSignOnObject !== undefined) {
				clearResponseTimeout(signOnKey);
				nowSignedOn[signOnKey] = true;
				activeSignOnObject.signOnData.validationRequired = false;

				//  save accepted data in storage for future use;
				Logger.system.debug(
					"APPLICATION LIFECYCLE:STARTUP:appAcceptSignOn Saving in Storage",
					activeSignOnObject.signOnData
				);

				myCrypto.encryptString(JSON.stringify(activeSignOnObject.signOnData), (encryptedData: string) => {
					Logger.system.debug(
						"APPLICATION LIFECYCLE:STARTUP:appAcceptSignOn After Encryption",
						activeSignOnObject.signOnData
					);
					StorageClient.save({
						topic: "finsemble",
						key: getSignOnStorageKey(signOnKey),
						value: encryptedData,
					});

					for (let i = 0; i < activeSignOnObject.queuedQuerys.length; i++) {
						Logger.system.debug("authentication.appAcceptSignOn", i, activeSignOnObject.signOnData);
						activeSignOnObject.queuedQuerys[i].sendQueryResponse(null, activeSignOnObject.signOnData);
					}

					delete activeSignOns[signOnKey];
				});
			} else {
				console.warn("APPLICATION LIFECYCLE:STARTUP:authentication.appAcceptSignOn for inactive signOnKey", signOnKey);
			}
		});

		// handles client appRejectSignOn request
		RouterClient.addListener("authentication.appRejectSignOn", (err, transmitEvent) => {
			Logger.system.debug("APPLICATION LIFECYCLE:STARTUP:authentication.appRejectSignOn", err, transmitEvent);
			const { signOnKey } = transmitEvent.data;
			const activeSignOnObject = activeSignOns[signOnKey];
			if (activeSignOnObject !== undefined) {
				clearResponseTimeout(signOnKey);
				for (let i = 0; i < activeSignOnObject.queuedQuerys.length; i++) {
					activeSignOnObject.queuedQuerys[i].sendQueryResponse("SignOn Rejected and Terminating", null);
				}
				delete activeSignOns[signOnKey];
			} else {
				console.warn("APPLICATION LIFECYCLE:STARTUP:authentication.appRejectSignOn for inactive signOnKey", signOnKey);
			}
		});
		autoSignOnConfig = FINSEMBLE_CONFIG.autoSignOn || { hardcoded: {} };
		Logger.system.log("APPLICATION LIFECYCLE:STARTUP:hardcoded", autoSignOnConfig);
		myCrypto = new MyCrypto(keyPart1 + keyPart2, () => {
			Logger.system.debug("STARTUP:AuthenticationService:STARTUP:MyCrypto callback");
			startSignOnCallback();
		});
	}
}

export default new AuthenticationService();
